import { addMinutes } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/
const TIME_REGEX = /^\d{2}:\d{2}(:\d{2})?$/

const isValidIanaTimeZone = timeZone => {
  if (typeof timeZone !== 'string' || timeZone.length === 0) {
    return false
  }
  try {
    const resolved = new Intl.DateTimeFormat(undefined, {
      timeZone
    }).resolvedOptions().timeZone
    return resolved === timeZone
  } catch {
    return false
  }
}

const validateAppointment = appointment => {
  if (!appointment || typeof appointment !== 'object') {
    throw new Error('Invalid appointment data: appointment is required')
  }
  if (!appointment.appointmentId) {
    throw new Error('Invalid appointment data: appointmentId is required')
  }
  if (!appointment.appointmentType) {
    throw new Error('Invalid appointment data: appointmentType is required')
  }
  if (!DATE_REGEX.test(appointment.date || '')) {
    throw new Error(
      `Invalid appointment data: date "${appointment.date}" must match YYYY-MM-DD`
    )
  }
  if (Number.isNaN(new Date(`${appointment.date}T00:00:00Z`).getTime())) {
    throw new Error(
      `Invalid appointment data: date "${appointment.date}" is not a real calendar date`
    )
  }
  if (!TIME_REGEX.test(appointment.time || '')) {
    throw new Error(
      `Invalid appointment data: time "${appointment.time}" must match HH:mm or HH:mm:ss`
    )
  }
  if (!isValidIanaTimeZone(appointment.timeZone)) {
    throw new Error(
      `Invalid appointment data: timeZone "${appointment.timeZone}" is not a valid IANA zone`
    )
  }
  const duration = Number(appointment.duration)
  if (!Number.isFinite(duration) || duration <= 0) {
    throw new Error(
      `Invalid appointment data: duration "${appointment.duration}" must be a positive number`
    )
  }
}

export const generateIcsFile = appointment => {
  validateAppointment(appointment)

  const dateWithTime = `${appointment.date}T${appointment.time}`
  const zonedDate = zonedTimeToUtc(dateWithTime, appointment.timeZone)
  const duration = Number(appointment.duration)
  const endZonedDate = addMinutes(zonedDate, duration)

  const formatIcsDate = date =>
    new Date(date).toISOString().replace(/[-:]|\.\d{3}/g, '')

  const escapeText = text => {
    if (!text) {
      return ''
    }
    return text.replace(
      /[\\;,\n\r\t]/g,
      char =>
        ({
          '\\': '\\\\',
          ';': '\\;',
          ',': '\\,',
          '\n': '\\n',
          '\r': '',
          '\t': '\\t'
        })[char]
    )
  }

  const foldLine = text => {
    const maxLength = 75
    let result = ''
    while (text.length > 0) {
      const part = text.substring(0, maxLength)
      text = text.substring(maxLength)
      result += part + (text.length > 0 ? '\r\n ' : '')
    }
    return result
  }

  const location = [
    appointment.departmentAddress?.street,
    appointment.departmentAddress?.city,
    appointment.departmentAddress?.state,
    appointment.departmentAddress?.zip,
    appointment.departmentAddress?.country
  ]
    .filter(Boolean)
    .join(', ')

  const description = escapeText(
    `Appointment type: ${appointment?.appointmentType || '-'}.
Provider: ${appointment?.providerName || '-'}
Clinic location: ${location}${
      appointment.patientInstructions
        ? `\nAppointment instructions: ${appointment?.patientInstructions}`
        : '-'
    }`
  )

  const descriptionLine = foldLine(`DESCRIPTION:${description}`)

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//MY CHOC//EN',
    'BEGIN:VEVENT',
    `DTSTAMP:${formatIcsDate(new Date())}`,
    `UID:${appointment.appointmentId}`,
    `SUMMARY:${escapeText(appointment.appointmentType)}`,
    descriptionLine,
    `DTSTART:${formatIcsDate(zonedDate)}`,
    `DTEND:${formatIcsDate(endZonedDate)}`,
    `LOCATION:${escapeText(location)}`,
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n')

  return icsContent
}

export const downloadIcsFile = (icsContent, fileName = 'appointment.ics') => {
  if (typeof document === 'undefined') {
    return
  }
  if (!icsContent) {
    return
  }
  const blob = new Blob([icsContent], { type: 'application/ics' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
